/**
 * Inbox modal — lists all conversations. Replaces `notifications-modal.js`.
 *
 * Renders a drawer with one card per peer-plate conversation. Tapping a card
 * opens the conversation thread view. Live-updates via the conversation
 * service's subscribeInbox API.
 */

import { createLogger } from '../../core/logger.js';
import { escape } from '../../shared/escape.js';
import { format as formatPlate } from '../../shared/plate.js';
import { timeAgo } from '../../shared/date.js';
import * as alert from '../../core/alert.js';
import * as conversationService from './conversation.service.js';
import * as conversationView from './conversation.view.js';
import * as phantomStore from '../phantom/phantom.store.js';
import * as phantomConfig from '../phantom/phantom.config.js';

const log = createLogger('inbox.view');

const OVERLAY_ID = 'messagingInboxOverlay';

let stylesInjected = false;
/** @type {(() => void) | null} */
let activeUnsubscribe = null;

export async function open() {
  const overlay = ensureModal();
  const list = /** @type {HTMLElement} */ (overlay.querySelector('.inbox-list'));
  list.innerHTML = '<div class="inbox-empty">טוען שיחות...</div>';
  openOverlay(overlay);

  // Initial render — always replace the "loading" placeholder so the user
  // doesn't see it forever if anything goes wrong.
  try {
    const summaries = await conversationService.list();
    renderList(list, mergePhantoms(summaries));
  } catch (err) {
    log.warn('initial list failed:', err);
    list.innerHTML = '<div class="inbox-empty">לא ניתן לטעון שיחות כעת.</div>';
  }

  // Live updates — re-fetch list when inbox changes. Wrapped in try/catch so
  // a permission denial doesn't bubble out and freeze the modal.
  if (activeUnsubscribe) activeUnsubscribe();
  try {
    activeUnsubscribe = conversationService.subscribeInbox(async () => {
      try {
        const summaries = await conversationService.list();
        renderList(list, mergePhantoms(summaries));
      } catch (e) { log.warn('refresh failed:', e); }
    });
  } catch (err) {
    log.warn('subscribeInbox failed:', err);
  }
}

/**
 * Merge phantom-plate threads (stored in `localStorage` only) into the
 * regular inbox summary list. Phantom entries are sorted alongside real
 * conversations by `updatedAt`. Each phantom carries `convId = 'phantom_<plate>'`
 * so the conversation view can detect and route to the local-only thread.
 *
 * No-op when the phantom feature is disabled (admin toggle).
 *
 * @param {Array<import('./conversation.service.js').ConversationSummary>} summaries
 * @returns {Array<import('./conversation.service.js').ConversationSummary>}
 */
function mergePhantoms(summaries) {
  if (!phantomConfig.isEnabled()) return summaries;
  const phantoms = phantomStore.getPhantomThreads();
  if (phantoms.length === 0) return summaries;

  // Real conversations always win — if a real conv exists for the same
  // plate (which can happen if the plate was registered after a phantom
  // thread was created), skip the phantom card.
  const realPlates = new Set(summaries.map((s) => s.peerPlate || '').filter(Boolean));
  const phantomSummaries = phantoms
    .filter((t) => !realPlates.has(t.plate))
    .map((t) => /** @type {import('./conversation.service.js').ConversationSummary} */ ({
      convId: `phantom_${t.plate}`,
      peerPlate: t.plate,
      peerUid: 'PHANTOM',
      unread: 0,
      lastMessage: {
        from: t.lastMessage?.from || 'me',
        body: t.lastMessage?.body || '',
        sentAt: t.lastMessage?.sentAt || t.updatedAt || 0,
      },
      updatedAt: t.updatedAt || 0,
    }));

  return [...summaries, ...phantomSummaries].sort((a, b) => {
    const ax = a.lastMessage?.sentAt ?? a.updatedAt ?? 0;
    const bx = b.lastMessage?.sentAt ?? b.updatedAt ?? 0;
    return bx - ax;
  });
}

export function close() {
  if (activeUnsubscribe) { activeUnsubscribe(); activeUnsubscribe = null; }
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) closeOverlay(overlay);
}

/**
 * Render the inbox list inside an existing host element instead of an
 * overlay. Used by the unified account drawer where "messages" is a tab.
 * The host gets a fresh `.inbox-list` injected (replacing any previous
 * content) so calling this is idempotent.
 *
 * @param {string} hostId
 */
export async function renderInto(hostId) {
  const host = document.getElementById(hostId);
  if (!host) { log.warn(`renderInto: #${hostId} not found`); return; }

  injectStyles();
  host.innerHTML = `<div class="inbox-list inbox-list--inline"><div class="inbox-empty">טוען שיחות...</div></div>`;
  const list = /** @type {HTMLElement} */ (host.querySelector('.inbox-list'));

  try {
    const summaries = await conversationService.list();
    renderList(list, mergePhantoms(summaries));
  } catch (err) {
    log.warn('inline list failed:', err);
    list.innerHTML = '<div class="inbox-empty">לא ניתן לטעון שיחות כעת.</div>';
  }

  if (activeUnsubscribe) activeUnsubscribe();
  try {
    activeUnsubscribe = conversationService.subscribeInbox(async () => {
      // If the host got detached (user switched tabs / closed drawer) stop
      // re-rendering — but DON'T tear down the subscription here: the next
      // renderInto call replaces it anyway.
      if (!document.body.contains(list)) return;
      try {
        const summaries = await conversationService.list();
        renderList(list, mergePhantoms(summaries));
      } catch (e) { log.warn('inline refresh failed:', e); }
    });
  } catch (err) {
    log.warn('subscribeInbox failed:', err);
  }
}

// ====================================================================
// internals
// ====================================================================

function ensureModal() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'drawer-overlay js-modal';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = `
    <aside class="drawer" role="dialog" aria-modal="true" aria-labelledby="inboxTitle">
      <header class="drawer-header">
        <h3 id="inboxTitle">הודעות</h3>
        <button class="drawer-close" data-close aria-label="סגור">✕</button>
      </header>
      <div class="drawer-body">
        <section class="drawer-content" style="padding:0;">
          <div class="inbox-list"></div>
        </section>
      </div>
      <footer class="drawer-footer">
        <button class="drawer-close-btn" data-close>סגור</button>
      </footer>
    </aside>
  `;
  document.body.appendChild(overlay);
  injectStyles();
  overlay.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', close));
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  return overlay;
}

/**
 * @param {HTMLElement} listEl
 * @param {import('./conversation.service.js').ConversationSummary[]} summaries
 */
function renderList(listEl, summaries) {
  if (summaries.length === 0) {
    listEl.innerHTML = '<div class="inbox-empty">אין הודעות עדיין.</div>';
    return;
  }
  listEl.innerHTML = summaries.map(cardHtml).join('');

  // Open the thread when the card body is tapped — but not when the trash
  // button is tapped (its handler stops propagation). Pass the stored convId
  // explicitly so threads opened after a plate replacement still resolve
  // (otherwise the view would recompute the convId from the new plate and
  // find an empty path).
  listEl.querySelectorAll('.inbox-card').forEach((card) => {
    card.addEventListener('click', () => {
      const plate = card.getAttribute('data-peer-plate') ?? '';
      const convId = card.getAttribute('data-conv-id') ?? '';
      // Admin/system threads have an empty `peerPlate` but a valid convId
      // (`admin_${uid}`). Open by convId in that case.
      if (plate || convId) void conversationView.open(plate, convId || undefined);
    });
  });

  // Trash button on each card — confirmation prompt, then soft-delete from
  // the user's inbox (the conversation still exists for the other party).
  listEl.querySelectorAll('.inbox-delete').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const card = /** @type {HTMLElement | null} */ (btn.closest('.inbox-card'));
      if (!card) return;
      const convId = card.getAttribute('data-conv-id') ?? '';
      const plate = card.getAttribute('data-peer-plate') ?? '';
      const plateLabel = plate ? formatPlate(plate) : 'שיחה זו';

      const confirmed = await alert.showConfirm({
        title: 'מחיקת שיחה',
        message: `למחוק את השיחה עם ${plateLabel}? השיחה תוסר מההודעות שלך (הצד השני עדיין יוכל לראות אותה).`,
        confirm: 'מחק',
        cancel: 'ביטול',
        danger: true,
      });
      if (!confirmed) return;

      // Phantom conversations live in localStorage only — `deleteFromInbox`
      // would try to write to `userConversations/{uid}/{convId}` which
      // doesn't exist. Route to the local store for those.
      if (convId.startsWith('phantom_')) {
        phantomStore.deletePhantomThread(plate);
        // No live subscription on phantom threads — re-render manually.
        try {
          const summaries = await conversationService.list();
          renderList(listEl, mergePhantoms(summaries));
        } catch (e) { log.warn('phantom delete refresh:', e); }
        return;
      }

      try {
        await conversationService.deleteFromInbox(convId);
        // The live subscription re-renders automatically — no manual refresh.
      } catch (err) {
        log.warn('delete failed:', err);
        alert.showToast('המחיקה נכשלה. נסה שוב.', { kind: 'error' });
      }
    });
  });
}

/** @param {import('./conversation.service.js').ConversationSummary} s */
function cardHtml(s) {
  const unreadBadge = s.unread > 0
    ? `<span class="inbox-unread">${s.unread}</span>`
    : '';
  const plateLabel = s.peerPlate ? formatPlate(s.peerPlate) : 'מנהל המערכת';
  return `
    <article class="inbox-card"
             data-peer-plate="${escape(s.peerPlate || '')}"
             data-conv-id="${escape(s.convId || '')}">
      <button class="inbox-delete" type="button" aria-label="מחק שיחה" title="מחק שיחה">🗑️</button>
      <div class="inbox-row1">
        <span class="inbox-plate" dir="ltr">${escape(plateLabel)}</span>
        ${unreadBadge}
      </div>
      <div class="inbox-preview">${escape(s.lastMessage.body || '')}</div>
      <div class="inbox-meta">${escape(timeAgo(s.lastMessage.sentAt || 0))}</div>
    </article>
  `;
}

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  // NOTE: selectors are unscoped so the same styles apply whether the list
  // is rendered inside the standalone `#messagingInboxOverlay` (open()) OR
  // inline inside the account drawer's `#accountContent` (renderInto()).
  // Previously they were scoped to `#${OVERLAY_ID}`, which meant the
  // inline-rendered list inside the account drawer was completely unstyled
  // — every conversation appeared as plain text run together.
  style.textContent = `
    .inbox-list {
      display: flex; flex-direction: column; gap: 10px;
      max-height: 70vh; overflow-y: auto;
      padding: 4px 2px;
    }
    /* Inline (account-drawer tab) variant — give the list room to breathe. */
    .inbox-list--inline { max-height: none; padding: 8px 2px; }

    .inbox-empty { text-align: center; padding: 30px 10px; color: #6b7a89; }

    .inbox-card {
      position: relative;
      background: #ffffff;
      padding: 14px 56px 14px 16px;
      border: 1px solid #e2e8f0;
      border-radius: 14px;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
      cursor: pointer;
      transition: transform .12s ease, box-shadow .15s ease, border-color .15s ease;
    }
    .inbox-card:hover {
      border-color: #14b8a6;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
      transform: translateY(-1px);
    }
    .inbox-card:active { transform: translateY(0); }

    .inbox-row1 {
      display: flex; justify-content: space-between; align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }
    .inbox-plate {
      font-weight: 800;
      font-size: 15px;
      letter-spacing: 0.04em;
      color: #0f172a;
      background: #fef9c3;
      border: 2px solid #1f2937;
      border-radius: 6px;
      padding: 3px 8px;
      display: inline-block;
    }
    .inbox-unread {
      background: #2196f3; color: #fff; font-size: 12px; font-weight: 700;
      padding: 3px 9px; border-radius: 999px; min-width: 22px; text-align: center;
      box-shadow: 0 2px 6px rgba(33, 150, 243, 0.35);
    }
    .inbox-preview {
      font-size: 14px; color: #2c3e50;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      line-height: 1.4;
    }
    .inbox-meta { font-size: 12px; color: #6b7a89; margin-top: 4px; }

    .inbox-delete {
      position: absolute;
      inset-inline-start: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 36px; height: 36px;
      border: 0;
      border-radius: 50%;
      background: transparent;
      color: #94a3b8;
      font-size: 18px;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: background .12s, color .12s;
    }
    .inbox-delete:hover { background: #fee2e2; color: #dc2626; }
    .inbox-delete:active { transform: translateY(-50%) scale(0.9); }
  `;
  document.head.appendChild(style);
}

/** @param {HTMLElement} overlay */
function openOverlay(overlay) {
  overlay.setAttribute('aria-hidden', 'false');
  overlay.classList.add('open');
  overlay.style.display = 'flex';
  document.body.classList.add('no-scroll');
}

/** @param {HTMLElement} overlay */
function closeOverlay(overlay) {
  overlay.setAttribute('aria-hidden', 'true');
  overlay.classList.remove('open');
  setTimeout(() => {
    if (overlay.getAttribute('aria-hidden') === 'true') overlay.style.display = 'none';
  }, 300);
  document.body.classList.remove('no-scroll');
}
